import React, { useCallback, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Switch,
  Typography,
  colors,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import debounce from "lodash.debounce";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable, { debounceSearchRender } from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey, orange } from "@mui/material/colors";
import { FormInputText } from "../../../components/form-components/FormInputText";
import SaveIcon from "@mui/icons-material/Save";
import { subCategorySchema } from "../../../app/schema";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { FormInputDropdown } from "../../../components/form-components/FormInputDropdown";
import {
  handlePageSettings,
  handleProductSearch,
  hideConfirmModal,
  showConfirmModal,
  showModal,
  handleProductFilters,
} from "../../../redux/counterSlice";
import GeneralImageUpload from "../../../components/form-components/GeneralImageUpload";
import { useNavigate } from "react-router-dom";
import { ThumbDown, ThumbsUpDown, ThumbUp } from "@mui/icons-material";
import { extractUniqueRegions } from "../../../app/utlils";
import { useTableHeight } from "../../../hooks/useTablePagination";
import { usePermission } from "../../../hooks/usePermission";
import axios from "axios";

function Products() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const permissions = usePermission("Products");
  const {
    taxList,
    categoryList,
    subCategoryList,
    allRegionList,
    searchSettings: settings,
    productFilters,
    flatPriceList,
    pageSettings: {
      product: { pageSize, pageNo },
    },
  } = useSelector((state) => state.counter);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const methods = useForm({
    resolver: yupResolver(subCategorySchema),
  });
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isValid },
  } = methods;

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // const [searchText, setSearchText] = useState("");
  // const [currentFilters, setCurrentFilters] = useState({});
  const tableHeight = useTableHeight();
  const [searchText, setSearchText] = useState(settings?.product || "");
  const [currentFilters, setCurrentFilters] = useState(
    productFilters?.apiFilters || {},
  );
  const [filterList, setFilterList] = useState(
    productFilters?.filterList || [[], [], [], [], [], [], [], [], []],
  );
  const [tableKey] = useState(() => Date.now());

  const loadInitialData = async (
    search = searchText,
    filtersOverride = currentFilters,
  ) => {
    const page = pageNo + 1;
    const activeFilters =
      search?.trim().length > 0
        ? { ...filtersOverride, q: search }
        : filtersOverride;
    await filterProductsHandler(activeFilters, page);
  };
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  const [priceEditProduct, setPriceEditProduct] = useState(null);
  const [selectedFlatPriceId, setSelectedFlatPriceId] = useState("");

  useEffect(() => {
    reset();
    const search = settings?.product || "";
    const savedApiFilters = productFilters?.apiFilters || {};
    const activeFilters =
      search?.trim().length > 0
        ? { ...savedApiFilters, q: search }
        : savedApiFilters;
    filterProductsHandler(activeFilters, pageNo + 1);
  }, [pageNo, pageSize]);

  // local searchText in sync with global redux search
  useEffect(() => {
    setSearchText(settings?.product || "");
  }, [settings]);

  const uniqueCategoryNames = Array.from(
    new Set(categoryList.map((item) => item?.category_name)),
  );
  const uniqueSubCategoryNames = Array.from(
    new Set(subCategoryList.map((item) => item?.sub_category_name)),
  );
  const uniqueRegions = Array.from(
    new Set(allRegionList.map((item) => item?.city?.name)),
  );

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => (
          <p>{pageNo * pageSize + dataIndex + 1}</p>
          // pageNo is 0‑indexed already, so no +1 needed there
        ),
      },
    },
    {
      name: "images",
      label: "Image",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const imageUrl = formData[dataIndex]?.images?.[0]?.app_product_image;
          return imageUrl ? (
            <PhotoProvider>
              <PhotoView key={dataIndex} src={imageUrl}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <Typography>-</Typography>
          );
        },
      },
    },
    {
      name: "product_name",
      label: "Product Name",
      options: {
        filter: false,
        customBodyRender: (value) => (
          <div
            title={value}
            style={{
              maxWidth: "200px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {value.length > 20 ? value.slice(0, 20) + "..." : value}
          </div>
        ),
      },
    },
    {
      name: "category.category_name",
      label: "Category",
      options: {
        customBodyRenderLite: (dataIndex) =>
          formData[dataIndex]?.category?.category_name ?? "-",
        filter: true,
        filterOptions: { names: uniqueCategoryNames },
      },
    },
    {
      name: "sub_category.sub_category_name",
      label: "Sub Category",
      options: {
        customBodyRenderLite: (dataIndex) =>
          formData[dataIndex]?.sub_category?.sub_category_name ?? "-",
        filter: true,
        filterOptions: { names: uniqueSubCategoryNames },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const imageUrl = formData[dataIndex]?.images?.[0]?.app_product_image;
          const status = formData[dataIndex]?.active_status;
          return (
            <Switch
              disabled={!imageUrl}
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(formData[dataIndex]?.id),
                  }),
                )
              }
              sx={{ mb: "5px" }}
            />
          );
        },
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
      },
    },

    {
      name: "best_selling",
      label: "Best Seller",
      options: {
        filter: true,
        display: "excluded",
        filterOptions: {
          names: ["Best Seller", "Not Best Seller"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Best Seller") && +value === 1) return false;
              if (filters.includes("Not Best Seller") && +value === 0)
                return false;
              return true;
            }
            return false;
          },
        },
      },
    },
    {
      name: "new_product",
      label: "New Product",
      options: {
        filter: true,
        display: "excluded",
        filterOptions: {
          names: ["New Arrival", "Not New Arrival"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("New Arrival") && +value === 1) return false;
              if (filters.includes("Not New Arrival") && +value === 0)
                return false;
              return true;
            }
            return false;
          },
        },
      },
    },

    {
      name: "region_id",
      label: "Region",
      options: {
        filterType: "multiselect",
        filter: true,
        display: "excluded",
        filterOptions: {
          names: uniqueRegions,
          logic(regionIdString, filters) {
            if (filters.length) {
              const regionIds = regionIdString
                .split(",")
                .map((id) => parseInt(id.trim(), 10));

              const selectedRegionIds = filters
                .map((filterName) => {
                  const region = allRegionList.find(
                    (region) => region.city?.name === filterName,
                  );
                  return region ? region.id : null;
                })
                .filter(Boolean);

              const isRegionPresent = regionIds.some((regionId) =>
                selectedRegionIds.includes(regionId),
              );

              return !isRegionPresent;
            }
            return false;
          },
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                return (
                  <div className="actionBtns">
                    <Button
                      disabled={status === 1 ? true : false}
                      aria-label="edit"
                      onClick={() => {
                        if (searchText !== "")
                          dispatch(handleProductSearch(searchText));
                        dispatch(
                          handlePageSettings({
                            screen: "product",
                            pageSize,
                            pageNo,
                          }),
                        ); // ✅ save page
                        navigate(
                          "edit-product-image/" + formData[dataIndex]?.id,
                        );
                      }}
                    >
                      <AddPhotoAlternateIcon
                        sx={{ color: status === 1 ? grey[600] : orange[500] }}
                        fontSize="medium"
                      />
                    </Button>
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1 ? true : false}
                        aria-label="edit"
                        onClick={() => editClickHandler(formData[dataIndex])}
                      >
                        <EditIcon
                          sx={{ color: status === 1 ? grey[600] : "secondary" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 1 ? true : false}
                        aria-label="edit"
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () =>
                                deleteClickHandler(formData[dataIndex]?.id),
                            }),
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: status === 1 ? grey[600] : "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
    // {
    //   name: "offer_fixed_price_id",
    //   label: "Flat Price",
    //   options: {
    //     filter: false,
    //     customBodyRenderLite: (dataIndex) => {
    //       const productId = formData[dataIndex]?.id;
    //       const offerId = formData[dataIndex]?.offer_fixed_price_id;
    //       const offerData = formData[dataIndex]?.offer_fixed_price;
    //       const isLinked = !!offerId;

    //       // No flat price → show just an edit icon
    //       if (!offerData) {
    //         return (
    //           <Button
    //             size="small"
    //             onClick={() => {
    //               setPriceEditProduct(formData[dataIndex]);
    //               setSelectedFlatPriceId("");
    //               setPriceEditOpen(true);
    //             }}
    //           >
    //             <EditIcon fontSize="small" sx={{ color: orange[500] }} />
    //           </Button>
    //         );
    //       }

    //       // Data present → switch + price with edit icon
    //       return (
    //         <Box
    //           sx={{
    //             display: "flex",
    //             flexDirection: "column",
    //             alignItems: "center",
    //           }}
    //         >
    //           <Switch
    //             size="small"
    //             color={isLinked ? "success" : "error"}
    //             checked={isLinked}
    //             onChange={() =>
    //               dispatch(
    //                 showConfirmModal({
    //                   msg: isLinked
    //                     ? "Deactivate Flat Price"
    //                     : "Activate Flat Price",
    //                   action: () => toggleOfferStatus(productId, offerId),
    //                 }),
    //               )
    //             }
    //           />
    //           <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
    //             <Typography variant="caption" sx={{ fontWeight: 500 }}>
    //               ₹{offerData?.fixed_price}
    //             </Typography>
    //             <EditIcon
    //               fontSize="inherit"
    //               sx={{
    //                 color: orange[500],
    //                 cursor: "pointer",
    //                 fontSize: "14px",
    //               }}
    //               onClick={() => {
    //                 setPriceEditProduct(formData[dataIndex]);
    //                 setSelectedFlatPriceId(offerId || "");
    //                 setPriceEditOpen(true);
    //               }}
    //             />
    //           </Box>
    //         </Box>
    //       );
    //     },
    //   },
    // },
  ];
  const handleClose = () => {
    setOpen(false);
    setOpenImage(false);
    setSelectedRow(null);
    reset();
  };
  const editClickHandler = (item) => {
    if (searchText !== "") dispatch(handleProductSearch(searchText));
    dispatch(handlePageSettings({ screen: "product", pageSize, pageNo })); // ✅ save page
    navigate("edit-product/" + item?.id);
  };
  const onSubmit = (data) => {
    // console.log("data", data);
    saveForm(data);
  };

  const toggleStatus = (id) => {
    setLoader(true);
    axios
      .get("/productSwitch/" + id)
      .then(function (response) {
        loadInitialData();
      })
      .catch(function (err) {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };
  const toggleOfferStatus = (productId, currentOfferId) => {
    setLoader(true);

    // Turning OFF → send null. Turning ON → send the first active flat price.
    const newOfferId = currentOfferId
      ? null
      : flatPriceList?.find((item) => item.active_status === 1)?.id;

    if (!currentOfferId && !newOfferId) {
      toast.error("No active flat price available to assign");
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
      return;
    }

    axios
      .post("/OfferSwitch/" + productId, {
        offer_fixed_price_id: newOfferId,
      })
      .then(function () {
        loadInitialData();
      })
      .catch(function (err) {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };
  const saveForm = async (data) => {
    setLoader(true);
    try {
      let response;
      if (selectedRow?.id) {
        response = await axios.put("/product/" + selectedRow?.id, data);
      } else {
        response = await axios.post("/product", data);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        setOpen(false);
        setSelectedRow((prev) => null);
        reset();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).map((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }

        dispatch(
          showModal({
            msg: errDesc,
            error: response?.data?.error,
          }),
        );
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        }),
      );
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };
  const deleteClickHandler = async (id) => {
    try {
      let response;
      if (id) {
        response = await axios.delete("/product/" + id);
        if (!response?.data?.error) {
          toast.success(response?.data?.message);
          loadInitialData();
          setOpen(false);
          setSelectedRow((prev) => null);
          reset();
        } else {
          let errDesc = "";
          if (response?.data?.data) {
            Object.values(response?.data?.data).map((error, index) => {
              errDesc =
                index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
            });
          } else {
            errDesc = response?.data?.message;
          }

          dispatch(
            showModal({
              msg: errDesc,
              error: response?.data?.error,
            }),
          );
        }
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        }),
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const filterProductsHandler = async (filters, page = pageNo + 1) => {
    try {
      setLoader(true);
      const params = {
        paginate: true,
        per_page: pageSize,
        page,
        ...filters,
      };

      const response = await axios.get("products/search", { params });

      const paginated = response?.data?.data || {};
      setFormData(paginated.data || []);
      setTotalCount(paginated.total || 0);
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoader(false);
    }
  };
  const CreateEditDialog = React.memo(() => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"sm"}>
        <DialogTitle className="text-primary">
          {selectedRow ? "Edit Product" : "New Product"}
        </DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={12}>
              <FormInputDropdown
                name="category_id"
                control={control}
                label="Category"
                type="single"
                options={categoryList?.map((item) => ({
                  id: item?.id,
                  name: item?.category_name,
                }))}
                errors={errors}
                defaultValue={selectedRow?.category_id}
                mandatory="true"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                name="sub_category_name"
                control={control}
                label="Sub Category Name"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.sub_category_name}
              />
            </Grid>

            <Grid item xs={12}>
              <FormInputText
                name="sub_category_description"
                control={control}
                label="Sub Category Description"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.sub_category_description}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputText
                name="hsn"
                control={control}
                label="HSN Code"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.hsn}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputDropdown
                name="tax_id"
                control={control}
                label="Tax"
                type="single"
                options={taxList?.map((item) => ({
                  id: item?.tax_id,
                  name: `${item?.tax_percentage} %`,
                }))}
                errors={errors}
                defaultValue={selectedRow?.tax_id}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            disabled={!isValid}
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Saves
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  const UpdateImageDialog = () => {
    return (
      <Dialog open={openImage} onClose={handleClose} maxWidth={"xs"}>
        <DialogTitle className="text-primary">Update Image</DialogTitle>
        <Divider variant="middle" className="bg-primary" />

        <DialogContent>
          <Grid container columnSpacing={{ xs: 4 }}>
            <Grid item xs={12}>
              <GeneralImageUpload
                status={selectedRow?.sub_category_image}
                name={selectedRow?.id?.toString()}
                control={control}
                label={selectedRow?.category_name}
                setValue={setValue}
                type="text"
                defaultValue={selectedRow?.sub_category_image}
                loadInitialData={loadInitialData}
                url="imageUpdateSubCategory"
                formDataKey={{ id: "id", image: "sub_category_image" }}
                dimension="220 x 160"
                setOpenImage={setOpenImage}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // const options = {
  //   fixedHeader: true,
  //   tableBodyHeight: tableHeight,
  //   enableNestedDataAccess: ".",
  //   selectableRows: false,
  //   download: false,
  //   viewColumns: true,
  //   print: false,
  //   serverSide: true,
  //   filter: true,
  //   searchText,
  //   onSearchChange: (val) => {
  //     setSearchText(val);
  //     const filters = { ...currentFilters, q: val };
  //     dispatch(handlePageSettings({ screen: "product", pageSize, pageNo: 0 }));
  //     filterProductsHandler(filters, 1);
  //   },
  //   onFilterChange: (changedColumn, filterList, type) => {
  //     console.log(filterList);
  //     const filters = {};
  //     if (filterList[3]?.length && filterList[3][0] !== "All") {
  //       const selectedCategory = categoryList.find(
  //         (cat) => cat.category_name === filterList[3][0]
  //       );
  //       if (selectedCategory) {
  //         filters.category_id = selectedCategory.id;
  //       }
  //     }

  //     if (filterList[4]?.length && filterList[4][0] !== "All") {
  //       const selectedSubCategoryName = filterList[4][0];
  //       const selectedSubCategory = subCategoryList.find(
  //         (item) => item?.sub_category_name === selectedSubCategoryName
  //       );
  //       if (selectedSubCategory && selectedSubCategory.id) {
  //         filters.sub_category_id = selectedSubCategory.id;
  //       }
  //     }

  //     // Status filter (column index 5)
  //     if (filterList[5]?.length && filterList[5][0] !== "All") {
  //       if (filterList[5][0] === "Active") filters.active_status = 1;
  //       if (filterList[5][0] === "Inactive") filters.active_status = 0;
  //     }
  //     if (filterList[6]?.length && filterList[6][0] !== "All") {
  //       if (filterList[6][0] === "Best Seller") filters.best_selling = 1;
  //       if (filterList[6][0] === "Not Best Seller") filters.best_selling = 0;
  //     }
  //     if (filterList[7]?.length && filterList[7][0] !== "All") {
  //       if (filterList[7][0] === "New Arrivals") filters.new_arrivals = 1;
  //       if (filterList[7][0] === "Not New Arrivals") filters.new_arrivals = 0;
  //     }
  //     if (filterList[8]?.length && filterList[8][0] !== "All") {
  //       const selectedRegionNames = filterList[8];
  //       const selectedRegionIds = allRegionList
  //         .filter((region) => selectedRegionNames.includes(region.city?.name))
  //         .map((region) => region.region_id);
  //       if (selectedRegionIds.length) {
  //         filters.region_id = selectedRegionIds.join(",");
  //       }
  //     }

  //     setCurrentFilters(filters);
  //     dispatch(
  //       handlePageSettings({
  //         screen: "product",
  //         pageSize: pageSize,
  //         pageNo: 0, // Reset to first page
  //       })
  //     );
  //     filterProductsHandler(filters, 1); // Always load first page for new filter
  //   },
  //   onSearchClose: () => dispatch(handleProductSearch("")),
  //   customSearchRender: debounceSearchRender(500), // 500ms debounce

  //   onSearchChange: (val) => {
  //     setSearchText(val);
  //     dispatch(
  //       handlePageSettings({
  //         screen: "product",
  //         pageSize: pageSize,
  //         pageNo: 0, // Reset to first page
  //       })
  //     );
  //     filterProductsHandler(currentFilters, 1);
  //   },
  //   onSearchClose: () => {
  //     setSearchText("");
  //     dispatch(handleProductSearch(""));
  //     dispatch(
  //       handlePageSettings({
  //         screen: "product",
  //         pageSize: pageSize,
  //         pageNo,
  //       })
  //     );
  //     loadInitialData();
  //   },
  //   // Paging
  //   rowsPerPage: pageSize,
  //   page: pageNo,
  //   count: totalCount,
  //   onChangeRowsPerPage: (newSize) =>
  //     dispatch(
  //       handlePageSettings({
  //         screen: "product",
  //         pageSize: newSize,
  //         pageNo: pageNo,
  //       })
  //     ),
  //   onChangePage: (currentPage) =>
  //     dispatch(
  //       handlePageSettings({
  //         screen: "product",
  //         pageSize: pageSize,
  //         pageNo: currentPage,
  //       })
  //     ),
  // };

  const options = {
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    enableNestedDataAccess: ".",
    selectableRows: false,
    download: false,
    viewColumns: true,
    print: false,
    serverSide: true,
    filter: true,
    searchText,
    filterList,

    // Combined search functionality - remove the first duplicate
    onSearchChange: (val) => {
      setSearchText(val);
      const filters = { ...currentFilters, q: val };
      dispatch(handlePageSettings({ screen: "product", pageSize, pageNo: 0 }));
      filterProductsHandler(filters, 1);
    },

    onSearchClose: () => {
      setSearchText("");
      dispatch(handleProductSearch(""));
      dispatch(
        handlePageSettings({
          screen: "product",
          pageSize: pageSize,
          pageNo: 0, // Reset to first page when clearing search
        }),
      );
      filterProductsHandler({}, 1); // Clear filters and load first page
    },

    customSearchRender: debounceSearchRender(500), // 500ms debounce

    // onFilterChange: (changedColumn, filterList, type) => {
    //   console.log(filterList);
    //   const filters = {};
    //   if (filterList[3]?.length && filterList[3][0] !== "All") {
    //     const selectedCategory = categoryList.find(
    //       (cat) => cat.category_name === filterList[3][0]
    //     );
    //     if (selectedCategory) {
    //       filters.category_id = selectedCategory.id;
    //     }
    //   }

    //   if (filterList[4]?.length && filterList[4][0] !== "All") {
    //     const selectedSubCategoryName = filterList[4][0];
    //     const selectedSubCategory = subCategoryList.find(
    //       (item) => item?.sub_category_name === selectedSubCategoryName
    //     );
    //     if (selectedSubCategory && selectedSubCategory.id) {
    //       filters.sub_category_id = selectedSubCategory.id;
    //     }
    //   }

    //   // Status filter (column index 5)
    //   if (filterList[5]?.length && filterList[5][0] !== "All") {
    //     if (filterList[5][0] === "Active") filters.active_status = 1;
    //     if (filterList[5][0] === "Inactive") filters.active_status = 0;
    //   }
    //   if (filterList[6]?.length && filterList[6][0] !== "All") {
    //     if (filterList[6][0] === "Best Seller") filters.best_selling = 1;
    //     if (filterList[6][0] === "Not Best Seller") filters.best_selling = 0;
    //   }
    //   if (filterList[7]?.length && filterList[7][0] !== "All") {
    //     if (filterList[7][0] === "New Arrivals") filters.new_arrivals = 1;
    //     if (filterList[7][0] === "Not New Arrivals") filters.new_arrivals = 0;
    //   }
    //   if (filterList[8]?.length && filterList[8][0] !== "All") {
    //     const selectedRegionNames = filterList[8];
    //     const selectedRegionIds = allRegionList
    //       .filter((region) => selectedRegionNames.includes(region.city?.name))
    //       .map((region) => region.region_id);
    //     if (selectedRegionIds.length) {
    //       filters.region_id = selectedRegionIds.join(",");
    //     }
    //   }

    //   setCurrentFilters(filters);
    //   dispatch(
    //     handlePageSettings({
    //       screen: "product",
    //       pageSize: pageSize,
    //       pageNo: 0, // Reset to first page
    //     })
    //   );
    //   filterProductsHandler(filters, 1); // Always load first page for new filter
    // },

    // Paging
    onFilterChange: (changedColumn, filterList, type) => {
      setFilterList(filterList);

      const filters = {};
      if (filterList[3]?.length && filterList[3][0] !== "All") {
        const selectedCategory = categoryList.find(
          (cat) => cat.category_name === filterList[3][0],
        );
        if (selectedCategory) filters.category_id = selectedCategory.id;
      }
      if (filterList[4]?.length && filterList[4][0] !== "All") {
        const selectedSubCategory = subCategoryList.find(
          (item) => item?.sub_category_name === filterList[4][0],
        );
        if (selectedSubCategory?.id)
          filters.sub_category_id = selectedSubCategory.id;
      }
      if (filterList[5]?.length && filterList[5][0] !== "All") {
        if (filterList[5][0] === "Active") filters.active_status = 1;
        if (filterList[5][0] === "Inactive") filters.active_status = 0;
      }
      if (filterList[6]?.length && filterList[6][0] !== "All") {
        if (filterList[6][0] === "Best Seller") filters.best_selling = 1;
        if (filterList[6][0] === "Not Best Seller") filters.best_selling = 0;
      }
      if (filterList[7]?.length && filterList[7][0] !== "All") {
        if (filterList[7][0] === "New Arrivals") filters.new_arrivals = 1;
        if (filterList[7][0] === "Not New Arrivals") filters.new_arrivals = 0;
      }
      if (filterList[8]?.length && filterList[8][0] !== "All") {
        const selectedRegionIds = allRegionList
          .filter((region) => filterList[8].includes(region.city?.name))
          .map((region) => region.region_id);
        if (selectedRegionIds.length)
          filters.region_id = selectedRegionIds.join(",");
      }

      setCurrentFilters(filters);

      // ✅ Save BOTH filterList (for UI) and apiFilters (for API) to Redux
      dispatch(handleProductFilters({ filterList, apiFilters: filters }));

      const shouldResetPage = changedColumn !== 5;
      if (shouldResetPage) {
        dispatch(
          handlePageSettings({ screen: "product", pageSize, pageNo: 0 }),
        );
        filterProductsHandler(filters, 1);
      } else {
        filterProductsHandler(filters, pageNo + 1);
      }
    },

    rowsPerPage: pageSize,
    page: pageNo,
    count: totalCount,
    onChangeRowsPerPage: (newSize) =>
      dispatch(
        handlePageSettings({
          screen: "product",
          pageSize: newSize,
          pageNo: pageNo,
        }),
      ),
    onChangePage: (currentPage) =>
      dispatch(
        handlePageSettings({
          screen: "product",
          pageSize: pageSize,
          pageNo: currentPage,
        }),
      ),
  };

  const activeCounter = formData.filter((i) => i.active_status === 1).length;
  const inActiveCounter = totalCount - activeCounter;
  const PriceEditDialog = () => (
    <Dialog
      open={priceEditOpen}
      onClose={() => setPriceEditOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className="text-primary">Change Flat Price</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Flat Price</InputLabel>
          <Select
            value={selectedFlatPriceId}
            label="Flat Price"
            onChange={(e) => setSelectedFlatPriceId(e.target.value)}
          >
            <MenuItem value="">-- SELECT --</MenuItem>
            {flatPriceList?.map((item) => (
              <MenuItem value={item?.id} key={item?.id}>
                ₹{item?.fixed_price}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setPriceEditOpen(false)}
          variant="contained"
          color="error"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => {
            setLoader(true);
            axios
              .post("/OfferSwitch/" + priceEditProduct?.id, {
                offer_fixed_price_id: selectedFlatPriceId || null,
              })
              .then(() => {
                toast.success("Flat price updated");
                setPriceEditOpen(false);
                loadInitialData();
              })
              .catch((err) => toast.error(err?.message))
              .finally(() => setLoader(false));
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
  return (
    <>
      <Box sx={{ position: "relative" }}>
        {loader && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              minHeight: "200px",
            }}
          >
            <Loader />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            visibility: loader ? "hidden" : "visible",
          }}
        >
          <h4 className="text-dark">Products</h4>
          <Stack direction="row" spacing={1}>
            <Chip
              icon={<ThumbsUpDown />}
              label={totalCount}
              color="primary"
              size="small"
            />
            <Chip
              icon={<ThumbUp />}
              label={activeCounter}
              color="success"
              size="small"
            />
            <Chip
              icon={<ThumbDown />}
              label={inActiveCounter}
              color="error"
              size="small"
            />
          </Stack>
        </Box>

        <div style={{ visibility: loader ? "hidden" : "visible" }}>
          <MUIDataTable
            key={tableKey}
            data={formData}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    searchText && dispatch(handleProductSearch(searchText));
                    navigate("new-product");
                  }}
                >
                  New Product
                </Button>
              ) : null
            }
          />
        </div>
      </Box>

      <CreateEditDialog />
      <UpdateImageDialog />
      <PriceEditDialog />
    </>
  );
}

export default Products;
